const NETWORK_FAILURE_PATTERN =
  /failed to fetch|network request failed|auth\/network-request-failed|load failed/i;
const NETWORK_BREADCRUMB_CATEGORIES = new Set(["fetch", "xhr", "http"]);

const getExceptionMessage = (event) =>
  String(event?.exception?.values?.[0]?.value || "");

const classifyNetworkTarget = (url, currentOrigin) => {
  if (url.origin === currentOrigin) return "lesson-launchpad";
  if (
    url.hostname === "securetoken.googleapis.com"
    || url.hostname === "identitytoolkit.googleapis.com"
  ) {
    return "firebase-auth";
  }
  if (url.hostname === "firestore.googleapis.com") return "firestore";
  if (url.hostname === "firebasestorage.googleapis.com") return "firebase-storage";
  if (url.hostname.endsWith(".cloudfunctions.net")) return "firebase-functions";
  if (url.hostname.endsWith(".googleapis.com")) return "google-api";
  return "external";
};

const getLatestNetworkBreadcrumb = (event) => {
  const breadcrumbs = Array.isArray(event?.breadcrumbs) ? event.breadcrumbs : [];
  return [...breadcrumbs].reverse().find((breadcrumb) => (
    NETWORK_BREADCRUMB_CATEGORIES.has(String(breadcrumb?.category || "").toLowerCase())
    && typeof breadcrumb?.data?.url === "string"
  ));
};

const getSecondsBeforeError = (eventTimestamp, breadcrumbTimestamp) => {
  const eventTime = Number(eventTimestamp);
  const breadcrumbTime = Number(breadcrumbTimestamp);
  if (!Number.isFinite(eventTime) || !Number.isFinite(breadcrumbTime)) return null;
  return Math.max(0, Math.round((eventTime - breadcrumbTime) * 1000) / 1000);
};

export function annotateNetworkFailureEvent(event, {
  currentHref = typeof window !== "undefined" ? window.location.href : "",
  navigatorObject = typeof navigator !== "undefined" ? navigator : null,
  documentObject = typeof document !== "undefined" ? document : null,
} = {}) {
  if (!event || !NETWORK_FAILURE_PATTERN.test(getExceptionMessage(event))) {
    return event;
  }

  const breadcrumb = getLatestNetworkBreadcrumb(event);
  let parsedUrl = null;
  try {
    parsedUrl = breadcrumb?.data?.url
      ? new URL(breadcrumb.data.url, currentHref || undefined)
      : null;
  } catch {
    parsedUrl = null;
  }

  let currentOrigin = "";
  try {
    currentOrigin = currentHref ? new URL(currentHref).origin : "";
  } catch {
    currentOrigin = "";
  }

  const target = parsedUrl
    ? classifyNetworkTarget(parsedUrl, currentOrigin)
    : "unknown";
  const transport = String(breadcrumb?.category || "unknown").toLowerCase();
  const browserOnline = typeof navigatorObject?.onLine === "boolean"
    ? navigatorObject.onLine
    : null;

  return {
    ...event,
    tags: {
      ...event.tags,
      network_failure_target: target,
      network_failure_transport: transport,
      ...(browserOnline == null ? {} : { browser_online: String(browserOnline) }),
    },
    contexts: {
      ...event.contexts,
      network_failure: {
        target,
        host: parsedUrl?.hostname || "unknown",
        transport,
        method: String(breadcrumb?.data?.method || "unknown").toUpperCase(),
        statusCode: breadcrumb?.data?.status_code ?? null,
        secondsBeforeError: getSecondsBeforeError(event.timestamp, breadcrumb?.timestamp),
        browserOnline,
        visibilityState: documentObject?.visibilityState || "unknown",
        effectiveConnectionType: navigatorObject?.connection?.effectiveType || "unknown",
      },
    },
  };
}
